/**
 * The MCP servers manager — list / add / enable-disable / remove the user's
 * bring-your-own MCP servers for Maximus, via the `/mcp` API. Auth tokens are
 * write-only (the API returns only `hasAuth`). Chrome-free and self-contained
 * (its own `om-mcp__*` styles) so it renders identically inside the dedicated
 * console page. The CALLER gates eligibility (`canUseMcpSettings`).
 */
import { useEffect, useState } from 'react';
import {
  deleteMcpServer,
  listMcpServers,
  putMcpServer,
  type McpServerView,
} from '../../agents/mcpApi.ts';
import { completeMcpOAuth, startMcpOAuth } from '../../agents/mcpApi.ts';
import {
  CUSTOM_SERVER_NOTICE,
  MCP_CATALOG,
  catalogEntry,
  isConnectableUrl,
} from '../../agents/mcpCatalog.ts';
import './McpServersManager.css';

const SERVER_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Recognized auth-scheme prefixes; a value under `Authorization` normally needs one. */
const AUTH_SCHEME = /^(?:bearer|basic|token|apikey)\s/i;

/** Mask a secret for display, keeping any leading auth scheme word visible. */
function maskAuthValue(raw: string): string {
  const value = raw.trim();
  const scheme = AUTH_SCHEME.exec(value)?.[0].trim();
  const secret = scheme === undefined ? value : value.slice(scheme.length).trim();
  const masked = secret.length <= 4 ? '••••' : `${secret.slice(0, 3)}${'•'.repeat(Math.min(8, secret.length - 3))}`;
  return scheme === undefined ? masked : `${scheme} ${masked}`;
}

/**
 * The `headers`-style config block the server will actually receive — the same
 * shape MCP server owners publish (e.g. GitHub's `"Authorization": "Bearer …"`),
 * so the user can diff it against their server's setup docs. Mirrors the backend
 * exactly: a header is sent only when a token is present (defaulting the name to
 * `Authorization`), and the token value is sent verbatim.
 */
function authPreview(url: string, header: string, token: string): string {
  const endpoint = url.trim().length > 0 ? url.trim() : 'https://mcp.example.com/sse';
  const hasToken = token.trim().length > 0;
  const name = header.trim().length > 0 ? header.trim() : 'Authorization';
  const headers = hasToken
    ? `  "headers": { ${JSON.stringify(name)}: ${JSON.stringify(maskAuthValue(token))} }`
    : '  "headers": {}  // no auth — public server only';
  return `{\n  "url": ${JSON.stringify(endpoint)},\n${headers}\n}`;
}

/** True when a token is set under `Authorization` but is missing a scheme prefix. */
function needsScheme(header: string, token: string): boolean {
  const name = header.trim().length > 0 ? header.trim() : 'Authorization';
  return (
    token.trim().length > 0 && /^authorization$/i.test(name) && !AUTH_SCHEME.test(token.trim())
  );
}

/**
 * The three API calls this panel makes, injectable so a BROWSER test can drive
 * the real component.
 *
 * The unit tests substitute the module with `vi.mock`, which has no equivalent
 * in a real browser; and the live client calls `getAccessToken()`, which THROWS
 * without a Cognito session — so a Playwright run cannot even reach the network
 * layer to intercept it. This seam is the smallest honest alternative to
 * shipping an untested panel. Production passes nothing and gets the real API.
 */
export interface McpServersApi {
  readonly list: typeof listMcpServers;
  readonly put: typeof putMcpServer;
  readonly remove: typeof deleteMcpServer;
  readonly startOAuth?: typeof startMcpOAuth;
  readonly completeOAuth?: typeof completeMcpOAuth;
}

export interface McpServersManagerProps {
  /** Injected ONLY by the DEV harness; defaults to the live API. */
  readonly api?: McpServersApi;
}

export default function McpServersManager({ api }: McpServersManagerProps = {}) {
  const listServers = api?.list ?? listMcpServers;
  const putServer = api?.put ?? putMcpServer;
  const removeServer = api?.remove ?? deleteMcpServer;
  const beginOAuth = api?.startOAuth ?? startMcpOAuth;
  const finishOAuth = api?.completeOAuth ?? completeMcpOAuth;
  const [servers, setServers] = useState<McpServerView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ serverId: '', url: '', name: '', authHeader: '', authToken: '' });
  const [picked, setPicked] = useState('');
  // A catalog entry declares how it signs in; a hand-typed server does not, so
  // it keeps today's header/none path.
  const needsOAuth = catalogEntry(picked)?.auth === 'oauth';

  /**
   * Fill the form from a catalog entry, or clear it for `Custom…`.
   *
   * The catalog only prefills — nothing about an entry is trusted downstream,
   * and what gets saved is still just an address and a sign-in kind.
   */
  const applyCatalogPick = (id: string): void => {
    setPicked(id);
    const entry = catalogEntry(id);
    if (entry === undefined) {
      setForm({ serverId: '', url: '', name: '', authHeader: '', authToken: '' });
      return;
    }
    setForm({
      serverId: entry.id,
      url: entry.url,
      name: entry.name,
      authHeader: entry.headerName ?? '',
      authToken: '',
    });
  };

  useEffect(() => {
    let live = true;
    void listServers()
      .then((s) => {
        if (live) {
          setServers(s);
        }
      })
      .catch(() => {
        // A passive load hiccup — or a first-time user with no config — must NOT
        // look like an error. Fall back to the neutral empty state (with a soft
        // note), reserving the red alert for actions the user actually took.
        if (live) {
          setLoadFailed(true);
          setServers([]);
        }
      });
    return () => {
      live = false;
    };
    // `listServers` is derived from the optional `api` prop, so it belongs in
    // the deps: it was a stable module import before the seam existed. In
    // production `api` is undefined and this resolves to the same function on
    // every render, so the effect still runs once — and a caller that swaps the
    // API genuinely should re-load.
  }, [listServers]);

  /**
   * Send the browser to the provider's approval screen.
   *
   * The server row is saved FIRST with `auth: 'oauth'` so that if the user
   * abandons the approval, the console still shows the server as connected but
   * not signed in — rather than losing what they typed.
   */
  const connectOAuth = (entry: { serverId: string; url: string; name?: string }): void => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await putServer({ ...entry, auth: 'oauth', enabled: true });
        const { authorizationUrl } = await beginOAuth({
          serverId: entry.serverId,
          url: entry.url,
          returnTo: `${window.location.origin}${window.location.pathname}`,
        });
        window.location.assign(authorizationUrl);
      } catch (cause) {
        setBusy(false);
        setError(cause instanceof Error ? cause.message : 'Sign-in could not be started.');
      }
    })();
  };

  // The return leg: the provider sends the browser back here with ?code=&state=.
  // Handled on the console rather than an API callback so the exchange runs
  // authenticated — see the CDK comment on the /mcp/oauth routes.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code === null || state === null) {
      return;
    }
    // Strip the credentials from the address bar immediately: an authorization
    // code in history or a shared URL is a live credential until it is spent.
    window.history.replaceState({}, '', window.location.pathname);
    setBusy(true);
    void finishOAuth({ code, state })
      .then(() => listServers())
      .then((s) => {
        setServers(s);
        setBusy(false);
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(cause instanceof Error ? cause.message : 'That sign-in could not be completed.');
      });
  }, [finishOAuth, listServers]);

  const run = (op: () => Promise<McpServerView[]>): void => {
    setBusy(true);
    setError(null);
    void op()
      .then((s) => setServers(s))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'That action could not be completed.'),
      )
      .finally(() => setBusy(false));
  };

  const addServer = (): void => {
    if (!SERVER_ID.test(form.serverId)) {
      setError('The id must be a short kebab-case slug, e.g. "github".');
      return;
    }
    if (!isConnectableUrl(form.url)) {
      // Caught here as well as shown inline: the server rejects it too, but a
      // round trip to learn "https only" is a worse experience than saying so.
      setError('The server address must be an https:// URL.');
      return;
    }
    if (needsOAuth) {
      // Sign-in servers leave via the provider's approval screen instead of
      // saving-and-done, so they take the whole other path.
      connectOAuth({
        serverId: form.serverId,
        url: form.url,
        ...(form.name.length > 0 ? { name: form.name } : {}),
      });
      return;
    }
    run(async () => {
      const next = await putServer({
        serverId: form.serverId,
        url: form.url,
        ...(form.name.length > 0 ? { name: form.name } : {}),
        // The backend sends `{authHeader}: {authToken}` verbatim and only when
        // BOTH are non-empty. So if the user supplies a token but no header,
        // default the header to `Authorization` — otherwise the token would be
        // silently dropped (the overwhelmingly common case is a Bearer token).
        ...(form.authToken.length > 0
          ? { authHeader: form.authHeader.trim().length > 0 ? form.authHeader.trim() : 'Authorization' }
          : form.authHeader.length > 0
            ? { authHeader: form.authHeader }
            : {}),
        ...(form.authToken.length > 0 ? { authToken: form.authToken } : {}),
        enabled: true,
      });
      setShowAdd(false);
      setForm({ serverId: '', url: '', name: '', authHeader: '', authToken: '' });
      return next;
    });
  };

  const toggle = (s: McpServerView): void => {
    run(() => putServer({ serverId: s.serverId, url: s.url, name: s.name, enabled: !s.enabled }));
  };

  return (
    <div className="om-mcp">
      {error !== null ? (
        <p className="om-mcp__error" role="alert">
          {error}
        </p>
      ) : null}

      {servers === null ? (
        <p className="om-mcp__loading">Loading your connected tools…</p>
      ) : servers.length === 0 ? (
        <p className="om-mcp__empty">
          {loadFailed
            ? 'Couldn’t load your connected tools just now — reopen this page to try again.'
            : 'No MCP servers connected yet. Add one below to give Maximus more tools.'}
        </p>
      ) : (
        <ul className="om-mcp__list">
          {servers.map((s) => (
            <li key={s.serverId} className="om-mcp__srv">
              <div className="om-mcp__srv-main">
                <span className="om-mcp__srv-name">{s.name ?? s.serverId}</span>
                <span className="om-mcp__srv-meta">
                  {s.url}
                  {s.hasAuth ? ' · authenticated' : ''}
                </span>
              </div>
              <button
                type="button"
                className={`om-mcp__toggle${s.enabled ? ' om-mcp__toggle--on' : ''}`}
                aria-pressed={s.enabled}
                aria-label={s.enabled ? `Turn ${s.serverId} off` : `Turn ${s.serverId} on`}
                disabled={busy}
                onClick={() => toggle(s)}
              />
              <button
                type="button"
                className="om-mcp__remove"
                disabled={busy}
                onClick={() => run(() => removeServer(s.serverId))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAdd ? (
        <div className="om-mcp__add">
          <label className="om-mcp__field">
            <span>Known server</span>
            <select
              className="om-mcp__catalog"
              value={picked}
              onChange={(e) => applyCatalogPick(e.target.value)}
            >
              <option value="">Custom…</option>
              {MCP_CATALOG.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                  {entry.verified ? '' : ' (experimental)'}
                </option>
              ))}
            </select>
            <small className="om-mcp__hint">
              Picking one fills in its address for you. {CUSTOM_SERVER_NOTICE.transport}
            </small>
          </label>
          <label className="om-mcp__field">
            <span>Server id</span>
            <input
              value={form.serverId}
              placeholder="github"
              onChange={(e) => setForm({ ...form, serverId: e.target.value })}
            />
          </label>
          <label className="om-mcp__field">
            <span>Server URL</span>
            <input
              value={form.url}
              placeholder="https://mcp.example.com/sse"
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
            <small className="om-mcp__hint">The server's MCP endpoint (SSE or streamable-HTTP URL), from its setup docs.</small>
            {form.url.length > 0 && !isConnectableUrl(form.url) ? (
              <small className="om-mcp__warn">Must be an https:// address.</small>
            ) : null}
          </label>
          <label className="om-mcp__field">
            <span>Display name (optional)</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <div className="om-mcp__row2">
            <label className="om-mcp__field">
              <span>Auth header (optional)</span>
              <input
                value={form.authHeader}
                placeholder="Authorization"
                onChange={(e) => setForm({ ...form, authHeader: e.target.value })}
              />
              <small className="om-mcp__hint">Header name the server expects — almost always <code>Authorization</code>.</small>
            </label>
            <label className="om-mcp__field">
              <span>Auth token (optional)</span>
              <input
                type="password"
                placeholder="Bearer ghp_…"
                value={form.authToken}
                onChange={(e) => setForm({ ...form, authToken: e.target.value })}
              />
              <small className="om-mcp__hint">The full header value, sent as-is. Include the scheme, e.g. <code>Bearer&nbsp;ghp_…</code>.</small>
            </label>
          </div>

          <div className="om-mcp__preview">
            <span className="om-mcp__preview-label">Maximus will connect like this</span>
            <pre className="om-mcp__preview-code">{authPreview(form.url, form.authHeader, form.authToken)}</pre>
            {needsScheme(form.authHeader, form.authToken) ? (
              <p className="om-mcp__preview-warn">
                Under <code>Authorization</code>, most servers expect a scheme prefix (e.g.{' '}
                <code>Bearer&nbsp;{'{token}'}</code>). Paste the full value your server's docs show, not just
                the raw key.
              </p>
            ) : (
              <p className="om-mcp__preview-note">
                Compare this against your MCP server's setup instructions — the header name and value format
                should match what its docs show (GitHub, for example, documents{' '}
                <code>"Authorization": "Bearer&nbsp;YOUR_PAT"</code>).
              </p>
            )}
          </div>

          <p className="om-mcp__caveat">
            Connect with a static token or API key (Bearer, Basic, or a custom header like{' '}
            <code>X-API-Key</code>). Servers that require an interactive OAuth sign-in, or more than one
            header, aren't supported here yet.
          </p>

          <div className="om-mcp__actions">
            <button type="button" className="om-mcp__btn" onClick={() => setShowAdd(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="om-mcp__btn om-mcp__btn--primary"
              disabled={busy || form.serverId.length === 0 || form.url.length === 0}
              onClick={addServer}
            >
              {busy ? 'Saving…' : needsOAuth ? 'Connect' : 'Add server'}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="om-mcp__add-btn" disabled={busy} onClick={() => setShowAdd(true)}>
          + Add MCP server
        </button>
      )}
    </div>
  );
}
